import type { GridColDef, GridColumnGroup } from '@mui/x-data-grid-pro';

import { formatDateDisplay } from './CharterUpgradePricingHelpers';
import {
  CABIN_CLASS_LABELS,
  type FlightUpgradeRow,
  VALID_UPGRADE_COMBINATIONS,
  comboKey,
} from './CharterUpgradePricingTypes';

function formatCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value}%`;
}

// Fixed columns (date, route, day windows)

const fixedColumns: Array<GridColDef<FlightUpgradeRow>> = [
  {
    field: 'depDate',
    headerName: 'Date',
    width: 115,
    editable: false,
    valueFormatter: (value: string) => formatDateDisplay(value),
  },
  {
    field: 'direction',
    headerName: 'Route',
    width: 120,
    editable: false,
  },
  {
    field: 'daysBeforeDepNonTierBronze',
    headerName: 'Bronze + non-tier',
    width: 130,
    type: 'number',
    editable: true,
    description: 'Days until departure window for non-tier & Bronze members',
  },
  {
    field: 'daysBeforeDepSilver',
    headerName: 'Silver',
    width: 70,
    type: 'number',
    editable: true,
    description: 'Days until departure window for Silver members',
  },
  {
    field: 'daysBeforeDepGoldPlatinum',
    headerName: 'Gold + Platinum',
    width: 110,
    type: 'number',
    editable: true,
    description: 'Days until departure window for Gold & Platinum members',
  },
];

// Dynamic combo columns (one group of 5 per valid upgrade pair)

const comboColumns: Array<GridColDef<FlightUpgradeRow>> = VALID_UPGRADE_COMBINATIONS.flatMap((combo) => {
  const key = comboKey(combo.from, combo.to);
  return [
    {
      field: `${key}_priceAdt`,
      headerName: 'ADT $',
      width: 85,
      type: 'number',
      editable: true,
      valueFormatter: formatCurrency,
    },
    {
      field: `${key}_chdPct`,
      headerName: 'CHD %',
      width: 75,
      type: 'number',
      editable: true,
      valueFormatter: formatPct,
      description: 'Child price as % of adult price',
    },
    {
      field: `${key}_infPct`,
      headerName: 'INF %',
      width: 75,
      type: 'number',
      editable: true,
      valueFormatter: formatPct,
      description: 'Infant price as % of adult price',
    },
  ] as Array<GridColDef<FlightUpgradeRow>>;
});

export const charterUpgradeColumns: Array<GridColDef<FlightUpgradeRow>> = [...fixedColumns, ...comboColumns];

// Column grouping model

export const charterUpgradeColumnGroupingModel: GridColumnGroup[] = [
  {
    groupId: 'flightInfo',
    headerName: 'Flight',
    children: [{ field: 'depDate' }, { field: 'direction' }],
  },
  {
    groupId: 'eligibility',
    headerName: 'Day Windows',
    children: [
      { field: 'daysBeforeDepNonTierBronze' },
      { field: 'daysBeforeDepSilver' },
      { field: 'daysBeforeDepGoldPlatinum' },
    ],
  },
  ...VALID_UPGRADE_COMBINATIONS.map((combo) => {
    const key = comboKey(combo.from, combo.to);
    return {
      groupId: key,
      headerName: `${CABIN_CLASS_LABELS[combo.from]} → ${CABIN_CLASS_LABELS[combo.to]}`,
      children: [{ field: `${key}_priceAdt` }, { field: `${key}_chdPct` }, { field: `${key}_infPct` }],
    };
  }),
];

// Editable pricing field names (for edit-mode highlighting)

export const EDITABLE_PRICING_FIELDS: string[] = VALID_UPGRADE_COMBINATIONS.flatMap((combo) => {
  const key = comboKey(combo.from, combo.to);
  return [`${key}_priceAdt`, `${key}_chdPct`, `${key}_infPct`];
});

export const EDITABLE_ELIGIBILITY_FIELDS = [
  'daysBeforeDepNonTierBronze',
  'daysBeforeDepSilver',
  'daysBeforeDepGoldPlatinum',
];
